/**
 * TestingSlide - Slide 6
 * Test prompts grid with likeness score summary
 */

import { Check, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { TestPromptCard } from "../components/TestPromptCard";
import type { TestResult } from "../types";

const THRESHOLD = 85;

interface TestingSlideProps {
  testResults: TestResult[];
  onRegenerate: (testId: string) => void;
  onAccept: (testId: string) => void;
}

export function TestingSlide({
  testResults,
  onRegenerate,
  onAccept,
}: TestingSlideProps) {
  // Calculate average score of accepted tests
  const acceptedTests = testResults.filter((t) => t.accepted);
  const allAccepted = acceptedTests.length === 4;

  const averageScore =
    acceptedTests.length > 0
      ? Math.round(
          acceptedTests.reduce((sum, t) => sum + t.score, 0) /
            acceptedTests.length,
        )
      : 0;

  const isAboveThreshold = averageScore >= THRESHOLD;

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <h2 className="text-2xl font-semibold text-foreground mb-3">
            Run unseen prompts.
          </h2>
          <p className="text-sm text-muted-foreground max-w-xl leading-relaxed">
            We test the trained LoRA against four prompts pulled from your
            script — including scenarios that weren&apos;t in the training pool.
            Aim for ≥ {THRESHOLD} on the likeness score.
          </p>
        </div>

        {/* Score summary box */}
        <div className="rounded-lg border border-white/10 bg-[#1a1a1a] p-4 text-right">
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">
            Likeness Score
          </p>
          <p
            className={cn(
              "text-3xl font-mono font-bold",
              averageScore > 0
                ? isAboveThreshold
                  ? "text-green-500"
                  : "text-amber-500"
                : "text-muted-foreground",
            )}
          >
            {averageScore > 0 ? `${averageScore} / 100` : "- / 100"}
          </p>
          {averageScore > 0 && (
            <div className="flex items-center justify-end gap-1 mt-1">
              {isAboveThreshold ? (
                <>
                  <Check className="w-3 h-3 text-green-500" />
                  <span className="text-[10px] text-green-500 font-medium">
                    ABOVE THRESHOLD
                  </span>
                </>
              ) : (
                <>
                  <AlertTriangle className="w-3 h-3 text-amber-500" />
                  <span className="text-[10px] text-amber-500 font-medium">
                    BELOW THRESHOLD
                  </span>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Test status */}
      <div className="flex items-center gap-2 mb-4">
        <span className="text-xs text-muted-foreground">
          {acceptedTests.length} of 4 tests accepted
        </span>
        {allAccepted && (
          <span className="px-2 py-0.5 rounded bg-green-500/10 text-green-500 text-[10px] font-medium">
            ALL COMPLETE
          </span>
        )}
      </div>

      {/* Test grid - 2x2 */}
      <div className="grid grid-cols-2 gap-4 max-w-3xl">
        {testResults.map((test) => (
          <TestPromptCard
            key={test.id}
            test={test}
            onRegenerate={() => onRegenerate(test.id)}
            onAccept={() => onAccept(test.id)}
          />
        ))}
      </div>

      {/* Helper text */}
      {!allAccepted && (
        <p className="text-xs text-muted-foreground mt-6 max-w-xl">
          Accept all 4 test results to continue. Use the regenerate button if
          you&apos;re not satisfied with a result.
        </p>
      )}

      {allAccepted && averageScore < THRESHOLD && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 mt-6 max-w-xl">
          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-amber-500 font-medium">
              Score below threshold
            </p>
            <p className="text-xs text-amber-500/70 mt-1">
              Your average likeness score is below {THRESHOLD}. You can still
              proceed, but results may be inconsistent.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
